// ABOUTME: Reads the current user's legacy column visibility for a data table
// ABOUTME: Read-only: superseded by useTableColumnState, kept to seed tables not migrated yet

import { useEffect, useState } from 'react';

const API_URL = '/api/settings/user/table-columns';

// Key the @ds/data-table package writes the column visibility to. It keeps writing it
// on every toggle, so it is read here as a last-resort fallback for users whose
// preferences never reached the server.
const getLocalStorageKey = (tableId: string) => `cmTable-visibility-${tableId}`;

export type ColumnsVisibility = Record<string, boolean>;

type UserTableColumns = Record<string, ColumnsVisibility>;

// Shared across every table on the page: the preferences of all tables live in a single
// user settings document, so one request serves all mounted tables.
let columnsCache: UserTableColumns | null = null;
let pendingRequest: Promise<UserTableColumns> | null = null;

const loadUserTableColumns = (): Promise<UserTableColumns> => {
    if (columnsCache) {
        return Promise.resolve(columnsCache);
    }

    if (!pendingRequest) {
        pendingRequest = fetch(API_URL, { credentials: 'include' })
            .then(async response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch table columns');
                }
                const result = await response.json();
                return (result.data ?? {}) as UserTableColumns;
            })
            .then(data => {
                columnsCache = data;
                pendingRequest = null;
                return data;
            })
            .catch(error => {
                pendingRequest = null;
                throw error;
            });
    }

    return pendingRequest;
};

/**
 * Reads the visibility saved by the @ds/data-table package in localStorage, used as
 * the initial value for users who customized a table before it was stored server-side.
 */
const readLocalStorageVisibility = (tableId: string): ColumnsVisibility | undefined => {
    try {
        const stored = localStorage.getItem(getLocalStorageKey(tableId));
        if (!stored) return undefined;
        const parsed = JSON.parse(stored);
        return Object.keys(parsed).length > 0 ? (parsed as ColumnsVisibility) : undefined;
    } catch {
        return undefined;
    }
};

type UseTableColumnsVisibilityResult = {
    /** Visibility to hand to the table as its initial state, undefined when nothing is saved. */
    visibility: ColumnsVisibility | undefined;
    /** False until the saved visibility is known; the table must not mount before then. */
    loaded: boolean;
};

/**
 * Legacy per-user column visibility of a single table, read from the user settings
 * document. Writes now go to `useTableColumnState`, which stores order and visibility
 * together; this entry is only read, to seed a table the user has not customized since.
 *
 * The table reads its visibility once, when mounting, so `loaded` gates the render:
 * a value arriving later would be ignored by the table.
 */
export const useTableColumnsVisibility = (
    tableId: string | undefined,
    enabled: boolean,
): UseTableColumnsVisibilityResult => {
    const isActive = !!tableId && enabled;

    const [visibility, setVisibility] = useState<ColumnsVisibility | undefined>();
    const [loaded, setLoaded] = useState(!isActive);

    useEffect(() => {
        if (!isActive || !tableId) {
            setLoaded(true);
            return undefined;
        }

        let cancelled = false;

        const applyVisibility = (stored: ColumnsVisibility | undefined) => {
            if (cancelled) return;
            setVisibility(stored);
            setLoaded(true);
        };

        loadUserTableColumns()
            // Nothing stored server-side for this table: fall back to whatever the browser
            // holds, so a visibility set before the server-side store existed is not lost.
            .then(columns =>
                applyVisibility(columns[tableId] ?? readLocalStorageVisibility(tableId)),
            )
            .catch(() => applyVisibility(readLocalStorageVisibility(tableId)));

        return () => {
            cancelled = true;
        };
    }, [isActive, tableId]);

    return { visibility, loaded };
};
